import type { Currency } from '@/lib/api/types';
import { type Dec, toDec } from './money';

// Port of backend/src/shared/arabic-words.ts, used only for the live preview
// on the payment screen; printed receipts use the text the API returns.

const ONES = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
const TEENS = ['عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const HUNDREDS = ['', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

interface Scale {
  value: number;
  one: string;
  two: string;
  plural: string;
  accusative: string;
}

const SCALES: Scale[] = [
  { value: 1e9, one: 'مليار', two: 'ملياران', plural: 'مليارات', accusative: 'ملياراً' },
  { value: 1e6, one: 'مليون', two: 'مليونان', plural: 'ملايين', accusative: 'مليوناً' },
  { value: 1e3, one: 'ألف', two: 'ألفان', plural: 'آلاف', accusative: 'ألفاً' },
];

function underHundred(n: number): string {
  if (n === 0) return '';
  if (n < 10) return ONES[n]!;
  if (n < 20) return TEENS[n - 10]!;
  const ones = n % 10;
  const tens = TENS[Math.floor(n / 10)]!;
  return ones ? `${ONES[ones]} و${tens}` : tens;
}

function underThousand(n: number, construct = false): string {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (h) parts.push(h === 2 && construct && rest === 0 ? 'مائتا' : HUNDREDS[h]!);
  if (rest) parts.push(underHundred(rest));
  return parts.join(' و');
}

function scaled(count: number, scale: Scale): string {
  if (count === 1) return scale.one;
  if (count === 2) return scale.two;
  const lastTwo = count % 100;
  if (lastTwo === 0) return `${underThousand(count, true)} ${scale.one}`;
  if (lastTwo === 1 || lastTwo === 2) {
    const hundreds = count - lastTwo;
    return `${underThousand(hundreds, true)} ${scale.one} و${lastTwo === 1 ? scale.one : scale.two}`;
  }
  if (lastTwo <= 10) return `${underThousand(count)} ${scale.plural}`;
  return `${underThousand(count)} ${scale.accusative}`;
}

export function integerToArabicWords(value: number): string {
  if (!Number.isSafeInteger(value) || value < 0 || value >= 1e12) return '';
  if (value === 0) return 'صفر';
  const parts: string[] = [];
  let rest = value;
  for (const scale of SCALES) {
    const count = Math.floor(rest / scale.value);
    if (count) parts.push(scaled(count, scale));
    rest %= scale.value;
  }
  if (rest) parts.push(underThousand(rest));
  return parts.join(' و');
}

const CURRENCY_WORDS: Record<Currency, { major: string; minor?: string }> = {
  IQD: { major: 'دينار عراقي' },
  LYD: { major: 'دينار ليبي', minor: 'درهم' },
};

/** "فقط مائتا ألف دينار عراقي لا غير" */
export function amountInArabicWords(value: Dec | string, currency: Currency): string {
  const amount = toDec(value);
  if (amount.isNegative()) return `سالب ${amountInArabicWords(amount.negated(), currency)}`;
  const words = CURRENCY_WORDS[currency];
  const major = amount.floor();
  let text = `فقط ${integerToArabicWords(major.toNumber())} ${words.major}`;
  if (words.minor) {
    const minor = amount.minus(major).times(1000).toDecimalPlaces(0).toNumber();
    if (minor > 0) text += ` و${integerToArabicWords(minor)} ${words.minor}`;
  }
  return `${text} لا غير`;
}
